import { TAudioFormat } from '@datatype/common/api-type';

/**
 * 브라우저 마이크 녹음기.
 *
 * 앱에서는 RN이 녹음을 담당하지만(브리지), 데스크톱 브라우저에는 그 경로가 없어 MediaRecorder로 직접 녹음한다.
 * getUserMedia는 보안 컨텍스트(HTTPS 또는 localhost)에서만 동작한다.
 */

// 브라우저마다 낼 수 있는 포맷이 다르다. 크롬·파이어폭스는 WebM, 사파리는 MP4만 낸다.
// 서버 presigned 서명에 쓸 값이므로 실제 녹음에 사용한 포맷을 그대로 올려야 한다.
const CANDIDATE_FORMATS: { mimeType: string; format: TAudioFormat }[] = [
  { mimeType: 'audio/webm;codecs=opus', format: 'WEBM' },
  { mimeType: 'audio/webm', format: 'WEBM' },
  { mimeType: 'audio/mp4', format: 'MP4' },
];

// 파형 표시를 RN metering(dB)과 맞추기 위한 값. 0(최대) ~ -50(무음) 구간으로 환산한다.
const METERING_MIN_DB = -50;

export interface IBrowserRecordingResult {
  blob: Blob;
  durationMs: number;
  format: TAudioFormat;
  contentType: string;
}

export const getIsBrowserRecordingSupported = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof MediaRecorder !== 'undefined';

const findSupportedFormat = () =>
  CANDIDATE_FORMATS.find((item) => MediaRecorder.isTypeSupported(item.mimeType)) ?? null;

/**
 * 마이크 스트림을 열고 녹음을 시작한다.
 *
 * getUserMedia 호출 시점에 브라우저가 권한 팝업을 띄운다. 거부하면 NotAllowedError가 난다.
 * handleMetering으로 음량(dB)을 흘려보내 앱과 같은 파형 UI를 쓰게 한다.
 */
export const startBrowserRecording = async (handleMetering: (metering: number) => void) => {
  const candidate = findSupportedFormat();
  if (!candidate) throw new Error('unsupported-format');

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const recorder = new MediaRecorder(stream, { mimeType: candidate.mimeType });
  const chunks: Blob[] = [];
  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  });

  // 음량 분석. AnalyserNode의 RMS를 dB로 바꿔 RN metering과 같은 단위로 맞춘다.
  const audioContext = new AudioContext();
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 512;
  audioContext.createMediaStreamSource(stream).connect(analyser);
  const buffer = new Float32Array(analyser.fftSize);

  let meteringTimer: number | null = window.setInterval(() => {
    analyser.getFloatTimeDomainData(buffer);
    const sum = buffer.reduce((total, sample) => total + sample * sample, 0);
    const rms = Math.sqrt(sum / buffer.length);
    handleMetering(rms > 0 ? Math.max(METERING_MIN_DB, 20 * Math.log10(rms)) : METERING_MIN_DB);
  }, 100);

  const startedAt = Date.now();
  recorder.start();

  // 스트림을 멈추지 않으면 탭의 마이크 표시가 남고 마이크가 점유된다.
  const release = () => {
    if (meteringTimer !== null) {
      window.clearInterval(meteringTimer);
      meteringTimer = null;
    }
    stream.getTracks().forEach((track) => track.stop());
    if (audioContext.state !== 'closed') audioContext.close();
  };

  return {
    finish: (): Promise<IBrowserRecordingResult> =>
      new Promise((resolve) => {
        recorder.addEventListener('stop', () => {
          release();
          resolve({
            blob: new Blob(chunks, { type: candidate.mimeType }),
            durationMs: Date.now() - startedAt,
            format: candidate.format,
            // presigned 서명은 코덱 파라미터 없는 값으로 이뤄지므로 PUT 헤더도 같은 값을 쓴다.
            contentType: candidate.format === 'WEBM' ? 'audio/webm' : 'audio/mp4',
          });
        });
        recorder.stop();
      }),
    cancel: () => {
      if (recorder.state !== 'inactive') recorder.stop();
      release();
    },
  };
};
